// @flow

import _ from 'lodash';
import moment from 'moment';

import ArrayUtils from '../../utils/ArrayUtils';
import DateUtils from '../../utils/DateUtils';
import Utils from '../../utils/Utils';

import ApiObject from '../ApiObject';
import ProductCategory from './ProductCategory';
import Product from './Product';
import ProductOption from './ProductOption';
import Menu from './Menu';
import CartModifier from './CartModifier';
import CategoryItem from './CategoryItem';
import MenuPart from './MenuPart';
import MenuPartProduct from './MenuPartProduct';
import UpsellingCategory from './UpsellingCategory';
import ItemSuggestion from './ItemSuggestion';

export default class RestaurantMenu extends ApiObject {
  restaurantId: string = '';

  rootCategoryId: string = '';

  categories: Array<ProductCategory> = [];

  products: Array<Product> = [];

  options: Array<ProductOption> = [];

  menus: Array<Menu> = [];

  cartModifiers: Array<CartModifier> = [];

  upsellingCategories: Array<UpsellingCategory> = [];

  suggestions: Array<ItemSuggestion> = [];

  posSyncDate: Moment = null;

  postJsonAssignation(data: Object) {
    // -- deserialize categories
    this.categories = [];
    _.each(data.categories, (orderCategoryData: Object) => {
      const orderCategory: ProductCategory = new ProductCategory();
      orderCategory.fromJson(orderCategoryData);
      this.categories.push(orderCategory);
    });

    // -- deserialize options
    this.options = [];
    _.each(data.options, (optionData: Object) => {
      const productOption: ProductOption = new ProductOption();
      productOption.fromJson(optionData);
      this.options.push(productOption);
    });

    // -- deserialize product
    this.products = [];
    _.each(data.products, (productData: Object) => {
      const product: Product = new Product();
      product.fromJson(productData);
      this.products.push(product);
    });

    // -- menus
    this.menus = [];
    _.each(data.menus, (menuData: Object) => {
      const menu: Menu = new Menu();
      menu.fromJson(menuData);
      this.menus.push(menu);
    });

    // -- cartModifiers
    this.cartModifiers = [];
    _.each(data.cartModifiers, (cartModifierData: Object) => {
      const cartModifier: CartModifier = new CartModifier();
      cartModifier.fromJson(cartModifierData);
      this.cartModifiers.push(cartModifier);
    });

    // --
    this.upsellingCategories = [];
    _.each(data.upsellingCategories, (orderCategoryData: Object) => {
      const orderCategory: UpsellingCategory = new UpsellingCategory();
      orderCategory.fromJson(orderCategoryData);
      this.upsellingCategories.push(orderCategory);
    });

    // --
    this.suggestions = [];
    _.each(data.suggestions, (suggestionData: Object) => {
      const suggestion: ItemSuggestion = new ItemSuggestion();
      suggestion.fromJson(suggestionData);
      this.suggestions.push(suggestion);
    });

    this.posSyncDate = DateUtils.fromMsTimestamp(data.posSyncDate);
  }

  toJSON() {
    const json = super.toJSON();

    // no need to send this value
    json.posSyncDate = null;

    return json;
  }

  //
  // Get Menu Restaurant for mode
  //

  /**
   * The differents filters that exists.
   * Warning: The order is important. This enum display each possibility in the good order.
   */
  static EFilterMenuRestaurantItems = {
    REMOVE_UNAVAILABLE_ITEMS: 'REMOVE_UNAVAILABLE_ITEMS',

    /**
     * Should be set on `filters` param after REMOVE_UNAVAILABLE_ITEMS
     */
    REMOVE_EMPTY_CATEGORIES: 'REMOVE_EMPTY_CATEGORIES',

    PUSH_BACK_NOT_IN_STOCK_ITEMS: 'PUSH_BACK_NOT_IN_STOCK_ITEMS',

    PUSH_BACK_NOT_IN_HOUR_ITEMS: 'PUSH_BACK_NOT_IN_HOUR_ITEMS',

    APPLY_CART_MODIFIERS_ON_ITEMS: 'APPLY_CART_MODIFIERS_ON_ITEMS',
  };

  getRestaurantMenuForModeWithFilters(
    mode: OrderMode,
    dueDate: Moment = null,
    filtersParams: Array<String> = []
  ) {
    let filters = filtersParams;
    if (_.isEmpty(filters)) {
      filters = [
        RestaurantMenu.EFilterMenuRestaurantItems.REMOVE_UNAVAILABLE_ITEMS,
        RestaurantMenu.EFilterMenuRestaurantItems.REMOVE_EMPTY_CATEGORIES,
        RestaurantMenu.EFilterMenuRestaurantItems.PUSH_BACK_NOT_IN_STOCK_ITEMS,
        RestaurantMenu.EFilterMenuRestaurantItems.PUSH_BACK_NOT_IN_HOUR_ITEMS,
        RestaurantMenu.EFilterMenuRestaurantItems.APPLY_CART_MODIFIERS_ON_ITEMS,
      ];
    }

    const menuRestaurantFiltered = this.copy();
    _.each(filters, filter => {
      switch (filter) {
        case RestaurantMenu.EFilterMenuRestaurantItems.REMOVE_EMPTY_CATEGORIES:
          menuRestaurantFiltered.removeEmptyCategories(mode);
          break;

        case RestaurantMenu.EFilterMenuRestaurantItems.REMOVE_UNAVAILABLE_ITEMS:
          menuRestaurantFiltered.removeUnavailableItems(mode, dueDate);
          break;
        case RestaurantMenu.EFilterMenuRestaurantItems
          .PUSH_BACK_NOT_IN_STOCK_ITEMS:
          menuRestaurantFiltered.pushBackNotInStockItems(mode);
          break;

        case RestaurantMenu.EFilterMenuRestaurantItems
          .PUSH_BACK_NOT_IN_HOUR_ITEMS:
          if (!_.isNull(dueDate)) {
            menuRestaurantFiltered.pushBackNotInHoursItems(mode, dueDate);
          }
          break;

        case RestaurantMenu.EFilterMenuRestaurantItems
          .APPLY_CART_MODIFIERS_ON_ITEMS:
          if (!_.isNull(dueDate)) {
            menuRestaurantFiltered.applyCartModifiersOnItems(mode, dueDate);
          }
          break;
      }
    });

    return menuRestaurantFiltered;
  }

  /**
   * Remove the items that are not available for the given `mode`.
   */
  removeUnavailableItems(mode: OrderMode, dueDate) {
    const productsIdsToRemove = [];

    // all the ids of the menus removed from this.menu and to remove on the categories.
    const menusIdsToRemove = [];

    this.products = _.filter(this.products, (product: Product) => {
      if (
        !product.isActivatedForMode(mode) ||
        !product.isActivatedForDate(dueDate)
      ) {
        productsIdsToRemove.push(product.id);
        return false;
      }

      return true;
    });

    let menus = [];

    // -- remove not activated menus
    menus = _.filter(this.menus, (menu: Product) => {
      if (!menu.isActivatedForMode(mode) || !menu.isActivatedForDate(dueDate)) {
        console.info(`remove ${menu.name}: not activated for mode`);
        menusIdsToRemove.push(menu.id);
        return false;
      }

      return true;
    });

    // We remove products that are not available from parts
    // if a part does not longer have products, we remove the menu
    const menusWithProductsFiltered = [];
    _.each(menus, (menu: Menu) => {
      let haveEmptyPart = false;

      _.each(menu.parts, (part: MenuPart) => {
        const items = [];
        let nbProductsAvailable = 0;

        _.each(part.products, (menuPartProduct: MenuPartProduct) => {
          const product = this.getProductWithId(menuPartProduct.id);

          if (!_.isNil(product)) {
            if (!_.includes(productsIdsToRemove, menuPartProduct.id)) {
              items.push(menuPartProduct);
            }

            const isAvailable =
              product.isAvailableForMode(mode) &&
              product.isActivatedForDate(dueDate);
            if (!isAvailable) {
              nbProductsAvailable++;
            }
          }
        });

        part.products = items;

        if (_.isEmpty(part.products)) {
          console.info(`${menu.name}: have empty part ${part.name}`);
          haveEmptyPart = true;
          return false; // quit loop because the menu will be remove
        }

        // all the remaining products are products that are not available.
        if (nbProductsAvailable === part.products.length) {
          console.info(
            `${menu.name}: have part with no available products ${part.name}`
          );
          haveEmptyPart = true;
          return false; // quit loop because the menu will be remove
        }
      });

      // if part is empty, remove menu
      if (haveEmptyPart) {
        console.info(`remove ${menu.name}: have empty part`);
        menusIdsToRemove.push(menu.id);
      } else {
        menusWithProductsFiltered.push(menu);
      }
    });

    this.menus = menusWithProductsFiltered;

    // -- remove menus from categories

    const categories = [];
    _.each(this.categories, (category: ProductCategory) => {
      const items = [];
      _.each(category.items, (item: CategoryItem) => {
        // is a menu
        if (item.isMenu()) {
          // id not in `menusIdsToRemove`.
          if (!_.includes(menusIdsToRemove, item.id)) {
            items.push(item);
          }
        }
        if (item.isCategory()) {
          items.push(item);
        }
        if (item.isProduct()) {
          // id not in `productsIdsToRemove`.
          if (!_.includes(productsIdsToRemove, item.id)) {
            items.push(item);
          }
        }
      });

      if (!_.isEmpty(items)) {
        category.items = items;
        categories.push(category);
      }
    });

    this.categories = categories;
  }

  /**
   * Remove the categories where there is no items
   */
  removeEmptyCategories(mode: OrderMode) {
    const categories = [];
    _.each(this.categories, (category: ProductCategory) => {
      if (!_.isEmpty(category.items)) {
        categories.push(category);
      }
    });

    this.categories = categories;
  }

  /**
   * Push to the end of the list the items that are not in stock
   * For now the menus has no stock information
   * so we handle only the products
   */
  pushBackNotInStockItems(mode: OrderMode) {
    const predicate = item => {
      return !item.haveStock();
    };

    this.pushBackWithPredicate(predicate, 'notInStock');
  }

  /**
   * Push to the end of the list the items that are not in available for the given dueDate
   */
  pushBackNotInHoursItems(mode: OrderMode, dueDate: Moment) {
    const predicate = (item): boolean => {
      return !item.isAvailableForDate(dueDate);
    };

    this.pushBackWithPredicate(
      predicate,
      `not in hours for date ${dueDate.format()}`
    );
  }

  getAllSubCategories(item, categoryIds = []) {
    if (categoryIds.indexOf(item.id) === -1) {
      const category = this.getCategoryWithId(item.id);

      if (!_.isNull(category)) {
        categoryIds.push(category.id);

        category.items.map(item => {
          let result = this.getAllSubCategories(item, categoryIds);
          categoryIds = _.merge(categoryIds, result);
        });
      }
    }

    return categoryIds;
  }

  getCartModifiersForItem(
    cartModifiers,
    item,
    itemType,
    mode,
    dueDate,
    allSubCategories
  ) {
    let cartModifiersItem = [];

    cartModifiers.forEach(cartModifier => {
      switch (cartModifier.target.type) {
        case CartModifier.TargetType.SPECIFIC_ITEMS:
          if (
            !_.isNil(cartModifier.target.items) &&
            !_.isNil(cartModifier.target.items.itemsList)
          ) {
            cartModifier.target.items.itemsList.forEach(itemTarget => {
              switch (itemTarget.type) {
                case CategoryItem.Type.PRODUCT:
                  if (itemTarget.id === item.id) {
                    cartModifiersItem.push(
                      cartModifier.getItemInformation(mode, dueDate)
                    );
                  }
                  break;
                case CategoryItem.Type.MENU:
                  if (itemTarget.id === item.id) {
                    cartModifiersItem.push(
                      cartModifier.getItemInformation(mode, dueDate)
                    );
                  }
                  break;
                case CategoryItem.Type.CATEGORY:
                  // apply cart modifier to the menu if it belongs to the category required
                  const categories =
                    itemType === CategoryItem.Type.MENU
                      ? this.getCategoriesWithMenuId(item.id)
                      : this.getCategoriesWithProductId(item.id);
                  let categoriesId = [];
                  categoriesId = categories.map(category => {
                    return category.id;
                  });
                  if (categoriesId.indexOf(itemTarget.id) > -1) {
                    cartModifiersItem.push(
                      cartModifier.getItemInformation(mode, dueDate)
                    );
                  }

                  // apply cart modifier if the menu belong to a sub-category that is contained in the category required
                  allSubCategories.forEach(parentCategory => {
                    if (itemTarget.id === parentCategory.id) {
                      parentCategory.items.forEach(itemIdFromSubCategory => {
                        if (item.id === itemIdFromSubCategory) {
                          cartModifiersItem.push(
                            cartModifier.getItemInformation(mode, dueDate)
                          );
                          return;
                        }
                      });
                    }
                  });
                  break;
                default:
                  return null;
                  break;
              }
            });
          }
          break;

        default:
          return null;
          break;
      }
    });

    return cartModifiersItem;
  }

  getFinalCartModifiers(cartModifiers, mode) {
    let firstCartModifier = true,
      skipCartModifiers = false,
      cartModifiersReturned = [];

    if (cartModifiers.length > 1) {
      // removing duplicated cart modifier for each product
      cartModifiers = cartModifiers.filter(
        (cartModifier, pos, cartModifiersArray) => {
          return (
            cartModifiersArray
              .map(mapCartModifier => mapCartModifier.id)
              .indexOf(cartModifier.id) === pos
          );
        }
      );

      // sort cart modifiers array by priority
      cartModifiers.sort((cartModifierFirst, cartModifierSecond) => {
        return cartModifierFirst.priority - cartModifierSecond.priority;
      });

      // mapping the cart modifiers array to check if we have to keep or not cart modifier
      cartModifiers.forEach(cartModifier => {
        if (
          _.isNil(cartModifier.modes) ||
          cartModifier.modes.indexOf(mode) !== -1
        ) {
          // if the cart modifier is for the order mode selected
          if (!cartModifier.combinable && firstCartModifier) {
            // if the first one is not combinable, we keep it and skip the rest
            cartModifiersReturned.push(cartModifier);
            firstCartModifier = false;
            skipCartModifiers = true;
          } else if (!skipCartModifiers && cartModifier.combinable) {
            // if we don't skip cart modifiers and it's combinable, we add it
            firstCartModifier = false;
            cartModifiersReturned.push(cartModifier);
          }
        }
      });
    } else {
      cartModifiersReturned = cartModifiers;
    }

    return cartModifiersReturned;
  }

  applyCartModifiersOnItems(mode: OrderMode, dueDate: Moment) {
    let products = [],
      menus = [];
    const cartModifiers = this.cartModifiers.filter(cartModifier => {
      return cartModifier.enabled === true;
    });

    if (cartModifiers.length > 0) {
      let allSubCategories = this.categories.map(item => {
        return {
          id: item.id,
          categories: this.getAllSubCategories(item).slice(1),
          items: [],
        };
      });

      allSubCategories.forEach((parentCategory, mainIndex) => {
        parentCategory.categories.forEach((subCategory, subIndex) => {
          const category = this.getCategoryWithId(subCategory);

          category.items.forEach((item, index) => {
            if (!item.isCategory()) {
              allSubCategories[mainIndex].items.push(item.id);
            }
          });
        });
      });

      this.products.forEach(product => {
        let cartModifiersProduct = this.getCartModifiersForItem(
          cartModifiers,
          product,
          CategoryItem.Type.PRODUCT,
          mode,
          dueDate,
          allSubCategories
        );
        product.cartModifiers = this.getFinalCartModifiers(
          cartModifiersProduct,
          mode
        );
        products.push(product);
      });
      this.products = products;

      this.menus.forEach(menu => {
        let cartModifiersMenu = this.getCartModifiersForItem(
          cartModifiers,
          menu,
          CategoryItem.Type.MENU,
          mode,
          dueDate,
          allSubCategories
        );
        menu.cartModifiers = this.getFinalCartModifiers(
          cartModifiersMenu,
          mode
        );
        menus.push(menu);
      });
      this.menus = menus;
    }
  }

  /**
   * @param predicate A function that returns true if we have to push back the product / menu
   * We set to true the `unavailable` of Menu / Product if the filtered consider them as unavailable
   * in order to simplify the front.
   */
  pushBackWithPredicate(predicate: Function, unavailableDebug = null) {
    // Here we save all the products id that are unavailable
    const unavailableItemsId: Array<String> = [];

    _.each(this.products, (product: Product) => {
      if (predicate(product)) {
        product.unavailable = true;
        product.unavailableDebug = unavailableDebug;
        unavailableItemsId.push(product.id);
      }
    });

    _.each(this.menus, (menu: Menu) => {
      if (predicate(menu)) {
        menu.unavailable = true;
        menu.unavailableDebug = unavailableDebug;
        unavailableItemsId.push(menu.id);
      }
    });

    // Now for each item we check if it should be push to the end of the list
    // ie if its id is not in the unavailableItemsId list
    // Therefore we should have now first the items with stock then products without it.

    // We push back the items not available from the categories
    _.each(this.categories, (category: ProductCategory) => {
      const availableItems = [];
      const unavailableItems = [];

      _.each(category.items, (item: CategoryItem) => {
        if (_.includes(unavailableItemsId, item.id)) {
          unavailableItems.push(item);
        } else {
          availableItems.push(item);
        }
      });

      category.items = _.concat(availableItems, unavailableItems);
    });

    // We push back the items not available from the categories
    _.each(this.upsellingCategories, (upsellingCategory: UpsellingCategory) => {
      const availableItems = [];
      const unavailableItems = [];

      _.each(upsellingCategory.items, (item: UpsellingCategoryItem) => {
        if (_.includes(unavailableItemsId, item.id)) {
          unavailableItems.push(item);
        } else {
          availableItems.push(item);
        }
      });

      upsellingCategory.items = _.concat(availableItems, unavailableItems);
    });

    // We push back the items not available from the menu parts
    _.each(this.menus, (menu: Menu) => {
      _.each(menu.parts, (part: MenuPart) => {
        const availableItems = [];
        const unavailableItems = [];

        _.each(part.products, (product: MenuPartProduct) => {
          if (_.includes(unavailableItemsId, product.id)) {
            unavailableItems.push(product);
          } else {
            availableItems.push(product);
          }
        });

        part.products = _.concat(availableItems, unavailableItems);
      });
    });
  }

  //
  // utils
  //

  getItem(item: CategoryItem): ?any {
    let res = null;
    switch (item.type) {
      case CategoryItem.Type.MENU:
        res = _.find(this.menus, (menu: Menu) => {
          return menu.id === item.id;
        });
        break;

      case CategoryItem.Type.PRODUCT:
        res = _.find(this.products, (product: Product) => {
          return product.id === item.id;
        });
        break;

      case CategoryItem.Type.CATEGORY:
        res = _.find(this.categories, (category: ProductCategory) => {
          return category.id === item.id;
        });
        break;

      default:
    }

    return _.isUndefined(res) ? null : res;
  }

  getItemById(id: string, type = null): ?any {
    let res = null;

    if (!type || type === CategoryItem.Type.PRODUCT) {
      res = _.find(this.products, (product: Product) => {
        return product.id === id;
      });
      if (!_.isNil(res)) {
        return res;
      }
    }

    if (!type || type === CategoryItem.Type.MENU) {
      res = _.find(this.menus, (menu: Menu) => {
        return menu.id === id;
      });
      if (!_.isNil(res)) {
        return res;
      }
    }

    if (!type || type === CategoryItem.Type.CATEGORY) {
      res = _.find(this.categories, (category: ProductCategory) => {
        return category.id === id;
      });

      return _.isUndefined(res) ? null : res;
    }

    return null;
  }

  getProductOptionWithId(optionId: string): ?ProductOption {
    return ArrayUtils.find(this.options, { id: optionId });
  }

  getProductOptionsWithIds(optionsIds: Array<String>): Array<ProductOption> {
    const options = [];

    _.each(optionsIds, (optionId: string) => {
      const option: ProductOption = this.getProductOptionWithId(optionId);
      if (!_.isNull(option)) {
        options.push(option);
      }
    });

    return options;
  }

  getRequiredProductOptionsWithIds(
    optionsIds: Array<String>
  ): Array<ProductOption> {
    const options = [];

    _.each(optionsIds, (optionId: string) => {
      const option: ProductOption = this.getProductOptionWithId(optionId);
      if (!_.isNull(option)) {
        options.push(option);
      }
    });

    const requiredOptions = _.filter(options, (option: ProductOption) => {
      return option.isRequired();
    });

    return requiredOptions;
  }

  getProductWithId(id: string): ?Product {
    const item: CategoryItem = new CategoryItem();
    item.id = id;
    item.type = CategoryItem.Type.PRODUCT;
    return this.getItem(item);
  }

  getProductsWithoutCategories(): Array<Product> {
    let itemsWithCategory = [];
    _.each(this.categories, (category: ProductCategory) => {
      itemsWithCategory = itemsWithCategory.concat(category.items);
    });

    itemsWithCategory = _.uniqBy(itemsWithCategory, e => {
      return e.id;
    });

    const productsIdsWithCategory = [];
    _.each(itemsWithCategory, (item: CategoryItem) => {
      if (item.isProduct()) {
        productsIdsWithCategory.push(item.id);
      }
    });

    const productsWithoutCategory = [];
    _.each(this.products, (product: Product) => {
      const found = _.find(productsIdsWithCategory, (productId: string) => {
        return productId === product.id;
      });

      if (!found) {
        productsWithoutCategory.push(product);
      }
    });

    return productsWithoutCategory;
  }

  getCategoriesWithProductId(productId: string): Array<ProductCategory> {
    const categories: Array<ProductCategory> = [];
    _.each(this.categories, (category: ProductCategory) => {
      const found = _.find(category.items, (item: CategoryItem) => {
        return item.isProduct() && productId === item.id;
      });

      if (!_.isUndefined(found)) {
        categories.push(category);
      }
    });

    return categories;
  }

  getCategoriesWithMenuId(menuId: string): Array<ProductCategory> {
    const categories: Array<ProductCategory> = [];
    _.each(this.categories, (category: ProductCategory) => {
      const found = _.find(category.items, (item: CategoryItem) => {
        return item.isMenu() && menuId === item.id;
      });

      if (!_.isUndefined(found)) {
        categories.push(category);
      }
    });

    return categories;
  }

  getProductsWithOptionId(optionId: string): Array<Product> {
    const products: Array<Product> = [];
    _.each(this.products, (product: Product) => {
      const found = _.find(product.optionsIds, (itemId: string) => {
        return itemId === optionId;
      });

      if (!_.isUndefined(found)) {
        products.push(product);
      }
    });

    return products;
  }

  getMenusWithProductId(productId: string): Array<Menu> {
    const menus: Array<Menu> = [];
    _.each(this.menus, (menu: Menu) => {
      if (menu.haveProduct(productId)) {
        menus.push(menu);
      }
    });

    return menus;
  }

  getMenuWithId(id: string): ?Menu {
    const item: CategoryItem = new CategoryItem();
    item.id = id;
    item.type = CategoryItem.Type.MENU;
    return this.getItem(item);
  }

  getCategoryWithId(id: string): ?ProductCategory {
    const item: CategoryItem = new CategoryItem();
    item.id = id;
    item.type = CategoryItem.Type.CATEGORY;
    return this.getItem(item);
  }

  //
  // products
  //

  addProduct(productToAdd: Product) {
    this.products.push(productToAdd);
  }

  updateProduct(productToUpdate: Product) {
    this.products = ArrayUtils.updateObject(this.products, productToUpdate, {
      id: productToUpdate.id,
    });
  }

  deleteProduct(productToRemove: Product) {
    this.products = ArrayUtils.deleteObject(this.products, {
      id: productToRemove.id,
    });

    // TODO: delete product from menus parts.
  }

  //
  //  products options
  //

  addProductOption(productOptionToAdd: ProductOption) {
    this.options.push(productOptionToAdd);
  }

  updateProductOption(productOptionToUpdate: ProductOption) {
    this.options = ArrayUtils.updateObject(
      this.options,
      productOptionToUpdate,
      { id: productOptionToUpdate.id }
    );
  }

  deleteProductOption(productOptionToRemove: ProductOption) {
    _.pull(this.options, productOptionToRemove);

    // TODO: delete from products
  }

  //
  // menu
  //

  addMenu(menuToAdd: Menu) {
    this.menus.push(menuToAdd);
  }

  updateMenu(menuToUpdate: Menu) {
    this.menus = ArrayUtils.updateObject(
      this.menus,
      menuToUpdate,
      (menu: Menu) => {
        return menu.id === menuToUpdate.id;
      }
    );
  }

  deleteMenu(menuToDelete: Menu) {
    this.menus = ArrayUtils.deleteObject(this.menus, { id: menuToDelete.id });

    // TODO: delete from categories
  }

  //
  // category
  //

  createCategoryWithName(categoryName: string) {
    const newCategory: ProductCategory = new ProductCategory();

    newCategory.name = categoryName;
    newCategory.id = Utils.generateUID();

    this.categories.unshift(newCategory);
  }

  addCategory(categoryToAdd: ProductCategory) {
    this.categories.push(categoryToAdd);
  }

  updateCategory(categoryToUpdate: ProductCategory) {
    this.categories = ArrayUtils.updateObject(
      this.categories,
      categoryToUpdate,
      { id: categoryToUpdate.id }
    );
  }

  deleteCategory(categoryToDelete: ProductCategory) {
    this.categories = ArrayUtils.deleteObject(this.categories, {
      id: categoryToDelete.id,
    });
  }

  moveUpCategory(categoryToMove: ProductCategory) {
    const fromIndex = _.findIndex(
      this.categories,
      (category: ProductCategory) => {
        return category.id === categoryToMove.id;
      }
    );

    const size = this.categories.length;
    const toIndex = fromIndex - 1 >= 0 ? fromIndex - 1 : size - 1;
    this.categories = ArrayUtils.moveOnArray(
      this.categories,
      fromIndex,
      toIndex
    );
  }

  moveDownCategory(categoryToMove: ProductCategory) {
    const fromIndex = _.findIndex(
      this.categories,
      (category: ProductCategory) => {
        return category.id === categoryToMove.id;
      }
    );

    const size = this.categories.length;
    const toIndex = fromIndex + 1 < size ? fromIndex + 1 : 0;
    this.categories = ArrayUtils.moveOnArray(
      this.categories,
      fromIndex,
      toIndex
    );
  }

  removeCategory(categoryToRemove: ProductCategory) {
    this.categories = _.remove(this.categories, (category: ProductCategory) => {
      return category.id !== categoryToRemove.id;
    });

    _.each(this.categories, (category: ProductCategory) => {
      category.removeItemWithId(categoryToRemove.id);
    });
  }

  getRootCategory(): ProductCategory {
    return this.getCategoryWithId(this.rootCategoryId);
  }

  getFirstLevelCategories() {
    const rootCategory = this.getRootCategory();
    if (!_.isNull(rootCategory)) {
      const categories = [];
      _.each(rootCategory.items, item => {
        if (item.isCategory()) {
          const category = this.getCategoryWithId(item.id);
          if (!_.isNull(category)) {
            categories.push(category);
          }
        }
      });
      return categories;
    }

    console.warn('Restaurant menu does not have a root category');
    return this.categories;
  }

  getFirstLevelCategoriesWithoutEmptyCategories() {
    const rootCategory = this.getRootCategory();
    if (!_.isNull(rootCategory)) {
      const categories = [];
      _.each(rootCategory.items, item => {
        if (item.isCategory()) {
          const category = this.getCategoryWithId(item.id);
          if (!_.isNull(category)) {
            _.each(category.items, itemCategory => {
              if (itemCategory.isCategory()) {
                const categoryChild = this.getCategoryWithId(itemCategory.id);
                if (!_.isNil(categoryChild)) {
                  categories.push(category);
                  return false;
                }
              } else {
                categories.push(category);
                return false;
              }
            });
          }
        }
      });
      return categories;
    }

    console.warn('Restaurant menu does not have a root category');
    return this.categories;
  }

  getUnusedCategoriesIds() {
    const categoriesIds = this.categories.map((category: ProductCategory) => {
      return category.id;
    });

    const childCategoriesIds = [];
    _.each(this.categories, (category: ProductCategory) => {
      _.each(category.items, item => {
        if (item.isCategory()) {
          childCategoriesIds.push(item.id);
        }
      });
    });

    let usedCategories = childCategoriesIds;
    usedCategories.push(this.rootCategoryId);
    usedCategories = _.uniq(childCategoriesIds);

    const diff = _.difference(categoriesIds, usedCategories);

    return diff;
  }

  removeCategoryFromRoot(category) {
    const rootCategory = this.getRootCategory();

    rootCategory.removeItemWithId(category.id);
  }

  getAllImages() {
    let images = [];

    images.push(
      ...this.categories
        .map((category: ProductCategory) => category.image)
        .filter(image => !_.isEmpty(image))
    );
    images.push(
      ...this.menus
        .map((menu: MenuOrProduct) => menu.image)
        .filter(image => !_.isEmpty(image))
    );
    images.push(
      ...this.products
        .map((product: MenuOrProduct) => product.image)
        .filter(image => !_.isEmpty(image))
    );

    return images;
  }

  //
  // upselling category
  //

  haveUpselling(): boolean {
    if (_.isEmpty(this.upsellingCategories)) {
      return false;
    }

    return this.getAvailableUpsellingCategories().length > 0;
  }

  getAvailableUpsellingCategories() {
    const categories = [];

    _.each(this.upsellingCategories, (upsellingCategory: UpsellingCategory) => {
      if (upsellingCategory.enabled) {
        let oneItemAvailable: boolean = false;

        _.each(upsellingCategory.items, (categoryItem: CategoryItem) => {
          const item: MenuOrProduct = this.getItem(categoryItem);

          if (!_.isNull(item) && !item.unavailable) {
            oneItemAvailable = true;
            return false; // quit loop
          }
        });

        if (oneItemAvailable) {
          categories.push(upsellingCategory);
        }
      }
    });

    return categories;
  }

  getUpsellingCategoryWithId(id: string): ?UpsellingCategory {
    return ArrayUtils.find(this.upsellingCategories, { id: id });
  }

  createUpsellingCategoryWithName(upsellingCategoryName: string) {
    const newUpsellingCategory: UpsellingCategory = new UpsellingCategory();

    newUpsellingCategory.name = upsellingCategoryName;
    newUpsellingCategory.id = Utils.generateUID();

    this.upsellingCategories.unshift(newUpsellingCategory);
  }

  addUpsellingCategory(upsellingCategoryToAdd: UpsellingCategory) {
    this.upsellingCategories.push(upsellingCategoryToAdd);
  }

  toggleUpsellingCategory(upsellingCategoryToUpdate: UpsellingCategory) {
    this.upsellingCategories = this.upsellingCategories.map(
      upsellingCategory => {
        upsellingCategory.enabled =
          upsellingCategory.id !== upsellingCategoryToUpdate.id
            ? false
            : upsellingCategoryToUpdate.enabled;
        return upsellingCategory;
      }
    );
  }

  updateUpsellingCategory(upsellingCategoryToUpdate: UpsellingCategory) {
    this.upsellingCategories = ArrayUtils.updateObject(
      this.upsellingCategories,
      upsellingCategoryToUpdate,
      { id: upsellingCategoryToUpdate.id }
    );
  }

  deleteUpsellingCategory(upsellingCategoryToDelete: UpsellingCategory) {
    this.upsellingCategories = ArrayUtils.deleteObject(
      this.upsellingCategories,
      { id: upsellingCategoryToDelete.id }
    );
  }

  moveUpUpsellingCategory(upsellingCategoryToMove: UpsellingCategory) {
    const fromIndex = _.findIndex(
      this.upsellingCategories,
      (upsellingCategory: UpsellingCategory) => {
        return upsellingCategory.id === upsellingCategoryToMove.id;
      }
    );

    const size = this.upsellingCategories.length;
    const toIndex = fromIndex - 1 >= 0 ? fromIndex - 1 : size - 1;
    this.upsellingCategories = ArrayUtils.moveOnArray(
      this.upsellingCategories,
      fromIndex,
      toIndex
    );
  }

  moveDownUpsellingCategory(upsellingCategoryToMove: UpsellingCategory) {
    const fromIndex = _.findIndex(
      this.upsellingCategories,
      (upsellingCategory: UpsellingCategory) => {
        return upsellingCategory.id === upsellingCategoryToMove.id;
      }
    );

    const size = this.upsellingCategories.length;
    const toIndex = fromIndex + 1 < size ? fromIndex + 1 : 0;
    this.upsellingCategories = ArrayUtils.moveOnArray(
      this.upsellingCategories,
      fromIndex,
      toIndex
    );
  }

  removeUpsellingCategory(upsellingCategoryToRemove: UpsellingCategory) {
    this.upsellingCategories = _.remove(
      this.upsellingCategories,
      (upsellingCategory: UpsellingCategory) => {
        return upsellingCategory.id !== upsellingCategoryToRemove.id;
      }
    );
  }

  //
  // Item suggestion
  //

  getItemSuggestionForItemId(id: string): ?ItemSuggestion {
    return ArrayUtils.find(this.suggestions, { id });
  }

  getAvailableItemSuggestionForItemId(id: string): ?ItemSuggestion {
    const itemSuggestion = this.getItemSuggestionForItemId(id);
    if (_.isNil(itemSuggestion)) {
      return null;
    }

    let oneItemAvailable: boolean = false;
    _.each(itemSuggestion.linkedItems, (suggestedItem: SuggestedItem) => {
      const item: MenuOrProduct = this.getItemById(
        suggestedItem.id,
        suggestedItem.type
      );

      if (!_.isNull(item) && !item.unavailable) {
        oneItemAvailable = true;
        return false; // quit loop
      }
    });

    return oneItemAvailable ? itemSuggestion : null;
  }

  haveItemSuggestionForItemId(id: string): boolean {
    const itemSuggestion = this.getItemSuggestionForItemId(id);
    return (
      !_.isUndefined(itemSuggestion) && !_.isEmpty(itemSuggestion.linkedItems)
    );
  }

  haveAvailableItemSuggestionForItemId(id: string): boolean {
    return !_.isNull(this.getAvailableItemSuggestionForItemId(id));
  }

  addItemSuggetion(suggestion: ItemSuggestion) {
    this.suggestions.push(suggestion);
  }

  updateItemSuggetion(suggestion: ItemSuggestion) {
    this.suggestions = ArrayUtils.updateObject(this.suggestions, suggestion, {
      id: suggestion.id,
    });
  }

  deleteItemSuggetion(suggestion: ItemSuggestion) {
    this.suggestions = ArrayUtils.deleteObject(this.suggestions, suggestion, {
      id: suggestion.id,
    });
  }
}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/menu/RestaurantMenu.js